import { useEffect, useState } from 'react'
import {
  Chip,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  useTheme,
  Collapse,
  Button,
  Tooltip
} from '@mui/material'
import { useLocation } from '@reach/router'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import { useLexnetStatusContext } from 'src/contexts/LexnetStatusContext'
import {
  LexnetIcon2,
  WarningIcon2,
  // LegalDocumentIcon,
  InboxLxcon2,
  AngleDownIcon2,
  CollapseIcon,
  ExpandIcon
} from 'src/components/shared/ui/Icons'
import Link from 'src/components/shared/ui/Link'
import {
  ACUSES,
  AVISOS_LEXNET,
  // ESCRITOS,
  MESSAGES
  // TRASLADOS
} from 'src/constants/routes'
import useAvisosLexnet from 'src/hooks/useAvisosLexnet'
import { AvisoLexnet } from 'src/interfaces'
import useStyles, { styles } from './useStyles'

export default function SideMenu(): JSX.Element {
  const classes = useStyles()
  const theme = useTheme()

  const [openOperational, setOpenOperational] = useState(false)
  const [openReceipt, setOpenReceipt] = useState(false)
  const [open, setOpen] = useState(true)
  const location = useLocation()
  const { t } = useTranslation()

  const avisosLexnetQuery = useAvisosLexnet(true)
  const { setNewNotificationsOpen } = useLexnetStatusContext()

  let newAvisosCount: string | null = null

  if (avisosLexnetQuery.isSuccess) {
    const { data } = avisosLexnetQuery
    const count = data.map((avisoLexnet: AvisoLexnet) => avisoLexnet.isNew).length
    newAvisosCount = count > 0 ? `${count}` : ''
  }

  useEffect(
    function() {
      if (newAvisosCount && newAvisosCount.length > 0) {
        setNewNotificationsOpen(true)
      }
    },
    // eslint-disable-next-line
    [avisosLexnetQuery.data, avisosLexnetQuery.isSuccess]
  )

  return (
    <>
      <Drawer
        className={`${classes.drawer} ${open ? '' : classes.closeDrawer}`}
        variant="permanent"
        open={open}
      >
        {/* <div className={classes.toolbar} /> */}

        <List className={classes.list}>
          {/*             AVISOS        */}

          <Tooltip
            arrow
            componentsProps={{ popper: styles.popper }}
            title={open ? '' : 'Avisos de LexNET'}
            placement="right"
          >
            <Link
              className={clsx(classes.listItem, classes.lexnetAvisos)}
              to={AVISOS_LEXNET()}
              data-selected={location.pathname.includes(AVISOS_LEXNET())}
            >
              <Box className={classes.listItemSelected} />
              <span className={classes.listItemIcon}>
                <WarningIcon2 htmlColor={theme.corporate.primaryColor} fontSize="small" />
              </span>
              <Box display="flex" flex="0 0 auto" alignItems="center" className="hideElement">
                <ListItemText primary="Avisos de LexNET" />
                {newAvisosCount && (
                  <Chip className={classes.listItemBadge} label={newAvisosCount} />
                )}
              </Box>
            </Link>
          </Tooltip>

          {/*          OPERATIVA          */}
          <Tooltip
            arrow
            componentsProps={{ popper: styles.popper }}
            title={open ? '' : 'Operativa LexNET'}
            placement="right"
          >
            <ListItemButton
              className={clsx(classes.listItem, classes.operativa)}
              onClick={() => {
                if (!openOperational && !open) {
                  setOpen(true)
                }

                setOpenOperational(!openOperational)
                setOpenReceipt(false)
              }}
              data-selected={location.pathname.includes(MESSAGES())}
            >
              <Box className={classes.listItemSelected} />
              <span className={classes.listItemIcon}>
                <LexnetIcon2 htmlColor={theme.corporate.primaryColor} fontSize="small" />
              </span>
              <Box display="flex" flex="0 0 auto" alignItems="center" className="hideElement">
                <ListItemText primary="Operativa LexNET" />
                <Box
                  className={classes.listItemIcon}
                  sx={{ transform: `rotate(${openOperational ? '180deg' : '0deg'})` }}
                >
                  <AngleDownIcon2 htmlColor={theme.corporate.primaryColor} fontSize="small" />
                </Box>
              </Box>
            </ListItemButton>
          </Tooltip>
          <Collapse in={openOperational} className={classes.listItemSubList}>
            <Link className={classes.subListItem} to={MESSAGES()}>
              <ListItemText primary="Todas las notificaciones" />
            </Link>
            <Link className={classes.subListItem} to={MESSAGES('pendientes')}>
              <ListItemText primary="Pendientes de firma" />
            </Link>
            <Link className={classes.subListItem} to={MESSAGES('firmadas')}>
              <ListItemText primary="Firmadas" />
            </Link>
          </Collapse>

          {/*          ACUSE RECIBO      */}
          <Tooltip
            arrow
            componentsProps={{ popper: styles.popper }}
            title={open ? '' : 'Acuse recibo'}
            placement="right"
          >
            <ListItemButton
              className={clsx(classes.listItem, classes.operativa)}
              onClick={() => {
                if (!openReceipt && !open) {
                  setOpen(true)
                }

                setOpenReceipt(!openReceipt)
                setOpenOperational(false)
              }}
              data-selected={location.pathname.includes(ACUSES())}
            >
              <Box className={classes.listItemSelected} />
              <span className={classes.listItemIcon}>
                <InboxLxcon2 htmlColor={theme.corporate.primaryColor} fontSize="small" />
              </span>
              <Box display="flex" flex="0 0 auto" alignItems="center" className="hideElement">
                <ListItemText primary="Acuse recibo" />
                <Box
                  className={classes.listItemIcon}
                  sx={{ transform: `rotate(${openReceipt ? '180deg' : '0deg'})` }}
                >
                  <AngleDownIcon2 htmlColor={theme.corporate.primaryColor} fontSize="small" />
                </Box>
              </Box>
            </ListItemButton>
          </Tooltip>
          <Collapse in={openReceipt} className={classes.listItemSubList}>
            <Link className={classes.subListItem} to={ACUSES()}>
              <ListItemText primary="Todos los acuses" />
            </Link>
            <Link className={classes.subListItem} to={ACUSES('aceptados')}>
              <ListItemText primary="Aceptados" />
            </Link>
            <Link className={classes.subListItem} to={ACUSES('pendientes')}>
              <ListItemText primary="Pendientes" />
            </Link>
            <Link className={classes.subListItem} to={ACUSES('rechazados')}>
              <ListItemText primary="Rechazados" />
            </Link>
          </Collapse>

          {/* <div className={clsx(classes.listItem, classes.operativa)}>
          <span className={classes.listItemIcon}>
            <LegalDocumentIcon htmlColor={theme.palette.primary.contrastText} />
          </span>
          <ListItemText primary="TRASLADO COPIA" />
        </div>
        <Link className={classes.listItem} to={TRASLADOS}>
          <span className={classes.listItemIcon}>
            <FolderIcon />
          </span>
          <ListItemText primary="Todos los traslados" />
        </Link>
        <div className={clsx(classes.listItem, classes.operativa)}>
          <span className={classes.listItemIcon}>
            <LexnetIcon htmlColor={theme.palette.primary.contrastText} />
          </span>
          <ListItemText primary="ESCRITOS" />
        </div>
        <Link className={classes.listItem} to={ESCRITOS('borradores')}>
          <span className={classes.listItemIcon}>
            <FolderIcon />
          </span>
          <ListItemText primary="Borradores" />
        </Link> */}
        </List>

        <Box flex="1 0 auto" display="flex" alignItems="flex-end" className={classes.collapseMenu}>
          <Tooltip title={open ? '' : 'Expandir menú'} arrow placement="right">
            <Button
              variant="text"
              className={clsx(classes.collapseMenuItem, 'collapseMenuItem')}
              onClick={() => {
                if (open) {
                  setOpenOperational(false)
                  setOpenReceipt(false)
                }
                setOpen(!open)
              }}
            >
              {open ? (
                <>
                  <span>{t('components.drawer.collapseMenu')}</span>
                  <CollapseIcon
                    htmlColor={theme.colors.primaryColor}
                    className={classes.collapseMenuIcon}
                  />
                </>
              ) : (
                <ExpandIcon
                  htmlColor={theme.colors.primaryColor}
                  className={classes.collapseMenuIcon}
                />
              )}
            </Button>
          </Tooltip>
        </Box>
      </Drawer>
    </>
  )
}
